// blogPosts.js — All blog post data for arhat.info
// Import this in BlogIndex.jsx and BlogPost.jsx


export const BLOG_POSTS = [

  // ── FIRE / Financial Independence ──────────────────────────────
  {
    slug:     'what-is-fire-number',
    title:    'What Is a FIRE Number and How Do You Calculate Yours?',
    excerpt:  'Your FIRE number is the single most important figure in early retirement planning. Learn exactly how to calculate it, why the 4% rule matters, and what it means for your timeline.',
    category: 'FIRE Planning',
    tag:      'fire',
    icon:     '🔥',
    readTime: '6 min',
    date:     '2026-01-15',
    content:  `
## What Is a FIRE Number?

Your FIRE number is the total investment portfolio value you need to retire and live off your investments indefinitely. It's the finish line of the Financial Independence, Retire Early movement.

The formula is simple:

**FIRE Number = Annual Expenses ÷ Withdrawal Rate**

At the classic 4% withdrawal rate, your FIRE number is **25× your annual expenses**.

| Annual Expenses | FIRE Number (4% rule) |
|----------------|----------------------|
| $30,000        | $750,000             |
| $50,000        | $1,250,000           |
| $75,000        | $1,875,000           |
| $100,000       | $2,500,000           |

## Why 25x? The 4% Rule Explained

The 4% rule comes from the Trinity Study (1998), which analysed historical US market data from 1926–1995. Researchers found that a portfolio of 50–75% stocks could sustain a 4% annual withdrawal rate for 30 years with a 95%+ success rate.

This means: if your portfolio is $1,250,000 and you spend $50,000/year, history suggests you'll almost certainly never run out of money over 30 years.

## How to Calculate Your FIRE Number

**Step 1: Track your actual annual expenses**

Be honest. Include:
- Housing (rent or mortgage + insurance + property tax)
- Food and groceries
- Transportation
- Healthcare and insurance
- Entertainment and subscriptions
- Travel
- Clothing and personal care
- Everything else

**Step 2: Decide on your withdrawal rate**

- **4%** — the classic rule, safe for 30-year retirements
- **3.5%** — more conservative, better for very long retirements (35–50 years)
- **3%** — ultra-conservative for 50+ year retirements or high uncertainty

**Step 3: Divide**

Annual expenses ÷ withdrawal rate = FIRE number.

$60,000 ÷ 0.04 = **$1,500,000**

## Does the 4% Rule Still Work?

The original Trinity Study used historical US data. Critics argue:
- Future returns may be lower (higher valuations today)
- Modern early retirees face 40–50 year retirements, not 30
- Sequence of returns risk is real

A more conservative approach for early FIRE is 3–3.5%, especially if you're retiring in your 30s or 40s. You can always use the [FIRE Calculator](/tools/finance/fire) to model different scenarios.

## What If Your FIRE Number Feels Impossible?

Most people see their FIRE number and feel overwhelmed. Don't be. The math compounds in your favour.

At a 7% real return with $30,000/year invested:
- **10 years**: ~$414,000
- **20 years**: ~$1,230,000
- **25 years**: ~$1,898,000

Consistency wins. Start where you are, invest what you can, and let compound growth do the heavy lifting.

## Use the Calculator

Try our free [FIRE Calculator](/tools/finance/fire) to find your number, see your projected FIRE date, and visualise your portfolio growth over time. No sign-up required.
    `,
  },

  {
    slug:     'coast-fire-explained',
    title:    'Coast FIRE Explained: The Milestone That Changes Everything',
    excerpt:  'Coast FIRE is the point where you can stop investing and still retire on time. It\'s the most underrated milestone in FIRE planning — here\'s exactly how it works.',
    category: 'FIRE Planning',
    tag:      'fire',
    icon:     '🌊',
    readTime: '5 min',
    date:     '2026-01-22',
    content:  `
## What Is Coast FIRE?

Coast FIRE is when your current investments — left untouched — will grow to your full FIRE number by your target retirement age. No more contributions needed. You just "coast" to the finish line.

**Coast FIRE Number = FIRE Target ÷ (1 + return rate)^years until retirement**

### Example

- FIRE target: $1,250,000
- Years to retirement: 25
- Expected return: 7%

Coast FIRE = $1,250,000 ÷ (1.07)^25 = **$232,000**

If you have $232,000 invested today, it will grow to $1.25M in 25 years with zero additional contributions.

## Why Coast FIRE Is a Game-Changer

Once you hit Coast FIRE:

1. **Work becomes optional for retirement** — you still need income to cover current expenses, but not to fund your future
2. **Career flexibility increases** — you can take lower-paying work you enjoy without worrying about retirement savings
3. **Stress drops significantly** — knowing retirement is "locked in" changes your relationship with work entirely

## Coast FIRE vs Regular FIRE

| | Coast FIRE | Full FIRE |
|---|---|---|
| Still need to work? | Yes (for expenses) | No |
| Portfolio contributions? | Optional | Not needed |
| Timeline | Earlier | Later |
| Lifestyle change | Moderate | Complete |

## How to Reach Coast FIRE Faster

- Increase your savings rate early in your career (time is your biggest asset)
- Maximise tax-advantaged accounts (RRSP, TFSA, 401k, IRA)
- Keep investment costs low (index funds)
- Avoid lifestyle inflation as income grows

The earlier you start, the lower your Coast FIRE number because time does more of the work.

## Calculate Your Coast FIRE Number

Use our free [Coast FIRE Calculator](/tools/finance/fire) to find your number and see how much you need today to coast to retirement.
    `,
  },

  {
    slug:     'savings-rate-fire-timeline',
    title:    'How Your Savings Rate Determines Your Retirement Date',
    excerpt:  'The single most powerful lever in FIRE planning isn\'t income — it\'s savings rate. Here\'s the math behind why going from 20% to 40% savings cuts your working years nearly in half.',
    category: 'FIRE Planning',
    tag:      'fire',
    icon:     '📊',
    readTime: '7 min',
    date:     '2026-02-01',
    content:  `
## The Savings Rate Is Everything

Income matters, but savings rate is what determines how quickly you reach financial independence. A high earner who saves 10% and a modest earner who saves 50% will reach FIRE at roughly similar times — the modest earner might actually win.

## The Savings Rate to FIRE Timeline Table

Assuming a 5% real return and starting from zero:

| Savings Rate | Years to FIRE |
|-------------|---------------|
| 10%         | ~43 years     |
| 20%         | ~32 years     |
| 30%         | ~25 years     |
| 40%         | ~20 years     |
| 50%         | ~17 years     |
| 60%         | ~13 years     |
| 75%         | ~7 years      |

The relationship is non-linear and dramatic. Going from 10% to 20% saves 11 years. Going from 50% to 75% saves 10 more years.

## Why Savings Rate Is Doubly Powerful

A higher savings rate does two things simultaneously:

1. **More money invested** → portfolio grows faster
2. **Lower lifestyle costs** → smaller FIRE number needed

If you currently spend $60,000/year, you need $1,500,000 to retire. If you cut spending to $40,000/year, you need only $1,000,000 — and you're saving $20,000 more each year to reach that smaller target.

## How to Calculate Your Savings Rate

**Net savings rate** = Annual savings ÷ Annual take-home pay × 100

Include employer contributions (RRSP match, pension) in savings. Include all income in take-home.

Example: Take-home $70,000, save $21,000 = **30% savings rate**

## Practical Ways to Increase Your Savings Rate

**Big wins (move the needle most):**
- Housing: downsize, house hack, move to a lower cost area
- Transportation: buy used cars, keep vehicles 10+ years, avoid car payments
- Income: negotiate raises, develop high-income skills

**Medium wins:**
- Cancel unused subscriptions
- Cook at home more often
- Shop insurance rates annually

**Small wins (still worth doing):**
- Pack lunch, make coffee at home
- Use cash-back credit cards (and pay in full)
- Buy generic brands

Use the [Savings Rate Calculator](/tools/finance/saving) to track your current rate and see how adjustments affect your FIRE timeline.
    `,
  },

  // ── Investing ──────────────────────────────────────────────────
  {
    slug:     'compound-interest-guide',
    title:    'Compound Interest: The Complete Guide with Real Examples',
    excerpt:  'Compound interest is why starting early matters so much more than the amount you invest. This guide breaks down exactly how it works, with real numbers and a free calculator.',
    category: 'Investing',
    tag:      'investing',
    icon:     '📈',
    readTime: '6 min',
    date:     '2026-02-10',
    content:  `
## What Is Compound Interest?

Compound interest is interest calculated on both your principal and all previously earned interest. Unlike simple interest (calculated only on principal), compound interest grows exponentially.

Simple interest on $10,000 at 8% for 10 years:
- Year 1: $800, Year 2: $800... Year 10: $800
- Total: $18,000

Compound interest on $10,000 at 8% for 10 years:
- Year 1: $800, Year 2: $864, Year 3: $933...
- Total: $21,589

The difference grows dramatically over time.

## The Power of Time

The most important variable in compound interest isn't the rate — it's time.

| Start Age | Monthly Investment | At Age 65 (7% return) |
|-----------|-------------------|----------------------|
| 25        | $500              | $1,321,000           |
| 35        | $500              | $607,000             |
| 45        | $500              | $262,000             |
| 25        | $250              | $660,000             |

Starting at 25 with $250/month beats starting at 35 with $500/month. Time is more powerful than amount.

## Compound Frequency Matters (A Little)

| Frequency | $10,000 at 10% for 10 years |
|-----------|---------------------------|
| Annual    | $25,937                   |
| Quarterly | $26,851                   |
| Monthly   | $27,070                   |
| Daily     | $27,179                   |

More frequent compounding is better, but the difference between monthly and daily is minimal. Annual vs monthly matters more.

## The Rule of 72

Divide 72 by your annual return to estimate how long it takes to double your money:

- 6% return: 72 ÷ 6 = **12 years to double**
- 8% return: 72 ÷ 8 = **9 years to double**
- 10% return: 72 ÷ 10 = **7.2 years to double**

## What Return Rate Should You Assume?

For long-term stock market investing:
- **Historical S&P 500**: ~10% nominal, ~7% real (after inflation)
- **Global diversified portfolio**: 6–8% real
- **Conservative planning**: 5–6% real

Use real returns (after inflation) so all figures are in today's purchasing power.

## Try the Calculator

Use our free [Compound Interest Calculator](/tools/finance/investing) to model your investments with any principal, monthly contribution, return rate, and time period.
    `,
  },

  {
    slug:     'dividend-investing-canada',
    title:    'Dividend Investing in Canada: A Practical Guide',
    excerpt:  'Canadian dividends receive preferential tax treatment that makes them particularly powerful for Canadian investors. Here\'s how dividend investing works and how to use it in your FIRE plan.',
    category: 'Investing',
    tag:      'investing',
    icon:     '💰',
    readTime: '7 min',
    date:     '2026-02-18',
    content:  `
## Why Canadian Dividends Are Special

Canadian eligible dividends receive the dividend tax credit, which significantly reduces the tax rate compared to ordinary income. At a 43% marginal rate (Ontario), eligible Canadian dividends are taxed at roughly 25–30% effective rate — much lower than interest income at the full marginal rate.

This makes Canadian dividend stocks particularly attractive for taxable accounts.

## The Dividend Tax Credit Advantage

| Income Type | $10,000 at 43% Marginal Rate |
|-------------|------------------------------|
| Interest income | ~$4,300 in tax |
| Eligible Canadian dividends | ~$2,500–3,000 in tax |
| Capital gains (50% inclusion) | ~$2,150 in tax |

Dividend income from Canadian companies sits between capital gains and interest for tax efficiency.

## Key Metrics for Dividend Investing

**Dividend Yield** = Annual Dividend ÷ Share Price × 100

A stock paying $2.40/year at $50/share has a 4.8% yield.

**Payout Ratio** = Annual Dividends ÷ Earnings Per Share × 100

Below 60%: sustainable. Above 80%: potentially risky. Above 100%: company paying more than it earns (unsustainable).

**Dividend Growth Rate**: how fast the dividend increases each year. Companies growing dividends 5–8% annually are often better investments than static high-yielders.

## Canadian Dividend Aristocrats

These are Canadian companies that have increased dividends for 5+ consecutive years:

- **Banks**: Royal Bank, TD, Scotiabank, BMO, CIBC, National Bank
- **Utilities**: Fortis, Emera, Hydro One
- **Telecoms**: BCE, Telus, Rogers
- **Pipeline/Energy**: Enbridge, TC Energy

Canadian banks in particular have remarkable dividend histories — many have paid dividends for over 100 years.

## Dividend Investing in a TFSA

The TFSA is ideal for Canadian dividend stocks because all dividends and growth are completely tax-free.

Important caveat: **US dividend stocks in a TFSA are subject to 15% US withholding tax** that cannot be recovered. Hold US dividend stocks in your RRSP instead, where the Canada-US tax treaty eliminates the withholding tax.

## The Yield Trap

High yield can be a warning sign, not a gift. A yield above 7–8% often indicates:
- The stock price has fallen (reflecting business problems)
- A dividend cut is coming
- The payout ratio is unsustainable

Always check the payout ratio and earnings trend alongside yield.

## Calculate Your Dividend Income

Use our free [Dividend Yield Calculator](/tools/finance/canada) to project your dividend income growth over time, see when dividends pay back your investment, and model different dividend growth rates.
    `,
  },

  // ── Canadian Finance ───────────────────────────────────────────
  {
    slug:     'rrsp-vs-tfsa-canada',
    title:    'RRSP vs TFSA: Which Should You Contribute to First?',
    excerpt:  'The RRSP vs TFSA question is one of the most common in Canadian personal finance. The answer depends on your income, tax bracket, and timeline — here\'s the complete breakdown.',
    category: 'Canadian Finance',
    tag:      'canadian',
    icon:     '🍁',
    readTime: '8 min',
    date:     '2026-03-01',
    content:  `
## The Core Difference

**RRSP**: Contribute with pre-tax dollars (tax deduction now), pay tax on withdrawal.

**TFSA**: Contribute with after-tax dollars (no deduction), but all growth and withdrawals are completely tax-free.

Both accounts shelter investment growth from annual taxation. The difference is timing: RRSP taxes you at withdrawal; TFSA taxes you now (via your marginal rate) and never again.

## The Rule of Thumb

**Contribute to RRSP when your current marginal rate is HIGHER than your expected retirement marginal rate.**

If you're in the 43% bracket now and expect to be in the 30% bracket in retirement, the RRSP wins — you defer 43% and pay back 30%.

If you're in the 25% bracket now and expect similar income in retirement, the TFSA often wins — no complexity, no future tax bill.

## Canadian Marginal Tax Rates 2026 (Ontario)

| Taxable Income | Marginal Rate |
|---------------|---------------|
| Up to $57,375 | ~30%          |
| $57,375–$100,392 | ~43%       |
| $100,392–$150,000 | ~47%      |
| Above $150,000 | ~53%         |

Generally: **earn above $60,000 → lean RRSP first**. **Earn below $55,000 → lean TFSA first**.

## When RRSP Wins

- High income now (43%+ marginal rate)
- Expect lower income in retirement
- Short on cash (RRSP refund helps fund the contribution)
- Have a spouse with lower income (spousal RRSP income-splitting strategy)
- Using the Home Buyers' Plan (withdraw up to $60,000 tax-free for first home)

## When TFSA Wins

- Lower income now (under $55,000)
- Expect similar or higher income in retirement
- Receiving GIS, OAS, or income-tested benefits in retirement (RRSP withdrawals increase income and can claw back benefits; TFSA does not)
- Need flexibility (TFSA withdrawals restore room the following year; RRSP withdrawals lose room permanently)
- Holding US dividend stocks — actually hold these in RRSP (withholding tax treaty applies)

## The FHSA: The New First Priority

The First Home Savings Account (FHSA), available since 2023, combines RRSP and TFSA advantages for first-time buyers:
- Contributions are tax-deductible (like RRSP)
- Withdrawals for a qualifying home are tax-free (like TFSA)
- $8,000/year limit, $40,000 lifetime

If you're a first-time buyer, maximise FHSA first, then RRSP, then TFSA.

## The Optimal Order (Most Canadians)

1. **Employer RRSP match** — always, it's free money
2. **FHSA** — if first-time buyer
3. **RRSP** — if income above ~$55,000
4. **TFSA** — fill remaining room
5. **Non-registered** — after all registered accounts maxed

## Use the Calculators

- [RRSP Calculator](/tools/finance/canada) — see your tax savings and growth projection
- [TFSA Calculator](/tools/finance/canada) — compare tax-free growth vs taxable account
    `,
  },

  {
    slug:     'canadian-gst-hst-guide',
    title:    'GST/HST in Canada: A Complete Guide for Freelancers and Small Businesses',
    excerpt:  'When do you need to register for GST/HST? What\'s the difference between GST and HST? How do you file? Everything Canadian freelancers and small business owners need to know.',
    category: 'Canadian Finance',
    tag:      'canadian',
    icon:     '🍁',
    readTime: '8 min',
    date:     '2026-03-10',
    content:  `
## What Is GST/HST?

**GST (Goods and Services Tax)** is the federal 5% tax applied to most goods and services sold in Canada.

**HST (Harmonised Sales Tax)** combines the federal GST with the provincial sales tax into a single tax collected by CRA. HST provinces:

| Province | HST Rate |
|----------|---------|
| Ontario | 13% |
| New Brunswick | 15% |
| Nova Scotia | 15% |
| Newfoundland & Labrador | 15% |
| Prince Edward Island | 15% |

**GST + PST provinces** (two separate taxes):
- British Columbia: 5% GST + 7% PST
- Saskatchewan: 5% GST + 6% PST
- Manitoba: 5% GST + 7% PST
- Quebec: 5% GST + 9.975% QST

**No provincial sales tax**: Alberta (5% GST only)

## When Do You Need to Register?

You must register for GST/HST if your **taxable revenues exceed $30,000 CAD** in a single calendar quarter OR over four consecutive calendar quarters.

This is the "small supplier threshold." Below $30,000, registration is optional.

### Should You Register Voluntarily?

Registering below $30,000 lets you claim Input Tax Credits (ITCs) — you get back the GST/HST you pay on business expenses. This can be worthwhile if you have significant business expenses.

Downside: you must charge GST/HST to clients and file returns.

## How to Register

1. Go to CRA's Business Registration Online (BRO)
2. Register for a Business Number (BN) and GST/HST account
3. Choose your filing period (monthly, quarterly, or annual)
4. Start charging GST/HST on all taxable supplies

## What Goes on the Invoice?

CRA requires registered businesses to show:
- Your GST/HST registration number
- The GST/HST amount charged (or state "GST/HST included")
- The tax rate if not the standard rate

For invoices over $150, you must also show the recipient's name and the description of goods/services.

## Input Tax Credits (ITCs)

ITCs let you recover the GST/HST you paid on business expenses. You net the GST/HST collected from clients against the GST/HST paid on expenses.

**Example:**
- Collected from clients: $5,000 GST/HST
- Paid on business expenses: $800 GST/HST
- Remit to CRA: $4,200

Common ITC-eligible expenses: professional fees, software subscriptions, office supplies, equipment, business portion of vehicle expenses.

## Calculate GST/HST Quickly

Use our free [GST/HST Calculator](/tools/business/gst) to calculate tax amounts for any Canadian province, the UK, Australia, or EU — instantly, no sign-up.
    `,
  },

  // ── Real Estate ────────────────────────────────────────────────
  {
    slug:     'rental-property-analysis',
    title:    'How to Analyse a Rental Property: Cap Rate, Cash Flow, and ROI',
    excerpt:  'Before buying a rental property, you need to run the numbers. This guide covers the three key metrics every investor should calculate and what makes a property worth buying.',
    category: 'Real Estate',
    tag:      'realestate',
    icon:     '🏠',
    readTime: '8 min',
    date:     '2026-03-20',
    content:  `
## The Three Numbers That Matter

Before buying any rental property, calculate these three metrics:

1. **Cap Rate** — property return independent of financing
2. **Cash-on-Cash Return** — return on your actual cash invested
3. **Monthly Cash Flow** — monthly income after all expenses

## 1. Cap Rate

**Cap Rate = Net Operating Income ÷ Property Value × 100**

Net Operating Income (NOI) = Gross rent − vacancy − operating expenses (not including mortgage)

**Example:**
- Monthly rent: $2,000 → annual: $24,000
- Vacancy (5%): −$1,200
- Operating expenses (taxes, insurance, maintenance): −$7,200
- NOI: $15,600
- Property value: $300,000
- **Cap Rate: 15,600 ÷ 300,000 = 5.2%**

### What's a Good Cap Rate?

| Cap Rate | Assessment |
|---------|-----------|
| Below 4% | Weak — relying heavily on appreciation |
| 4–6% | Acceptable in competitive markets |
| 6–8% | Good — solid cash flow |
| Above 8% | Excellent — strong cash flow |

In expensive markets like Toronto or Vancouver, 3–4% cap rates are common due to property values. In smaller Canadian cities, 6%+ is achievable.

## 2. Cash-on-Cash Return

**CoC Return = Annual Cash Flow ÷ Total Cash Invested × 100**

This is the most practical metric for leveraged real estate because it accounts for your mortgage.

**Example:**
- Down payment: $60,000 (20%)
- Closing costs: $5,000
- Total cash invested: $65,000
- Monthly rent: $2,000
- Monthly expenses (mortgage + taxes + insurance + maintenance): $1,700
- Monthly cash flow: $300 → annual: $3,600
- **CoC Return: 3,600 ÷ 65,000 = 5.5%**

A 6–8% CoC return is generally considered strong for residential rentals.

## 3. Monthly Cash Flow

**Cash Flow = Effective Rent − All Expenses**

All expenses include:
- Mortgage payment (principal + interest)
- Property tax
- Insurance
- Property management (8–12% if managed)
- Maintenance reserves (budget 1% of property value annually)
- Vacancy allowance (5–10%)
- Capital expenditure reserves (roof, HVAC, appliances)

**Never use "gross rent minus mortgage" as cash flow.** This common mistake ignores 30–40% of actual costs.

## The 1% Rule (Quick Screen)

Monthly rent ≥ 1% of purchase price

$300,000 property → needs $3,000+/month rent to pass the 1% rule.

It's a rough filter, not a final analysis. Most properties in expensive Canadian cities fail this test — investors in those markets rely on appreciation rather than cash flow.

## Run the Numbers

Use our free calculators:
- [Rental Property Calculator](/tools/finance/investing) — full 30-year projection
- [Cap Rate Calculator](/tools/finance/investing) — sensitivity analysis across price ranges
- [Cash-on-Cash Calculator](/tools/finance/investing) — CoC return and breakeven analysis
    `,
  },

  {
    slug:     'house-hacking-canada',
    title:    'House Hacking in Canada: How to Live for Free (or Close to It)',
    excerpt:  'House hacking is one of the most powerful wealth-building strategies available to young Canadians. Here\'s how it works, what to watch out for, and how to calculate if it makes sense for you.',
    category: 'Real Estate',
    tag:      'realestate',
    icon:     '🏠',
    readTime: '6 min',
    date:     '2026-03-28',
    content:  `
## What Is House Hacking?

House hacking means buying a property, living in part of it, and renting out the rest to offset your housing costs. Done well, your tenants cover most or all of your mortgage — you build equity while living for free or near-free.

## Common House Hacking Strategies

**Duplex/Triplex/Fourplex**: Buy a multi-unit property, live in one unit, rent the others. The most effective strategy — other units cover the full mortgage.

**Basement suite**: Buy a single-family home with a legal basement suite. Rent the basement; live upstairs.

**Rent by room**: Rent individual rooms to multiple tenants. Higher total rent but more management.

**ADU (Accessory Dwelling Unit)**: Build or renovate a garden suite or garage conversion and rent it.

## Why It's Powerful in Canada

1. **Low down payment**: Properties under 4 units qualify for CMHC-insured mortgages with as little as 5% down (primary residence rates apply when you live in the building)

2. **Rental income helps qualification**: Lenders can use rental income to help you qualify for a larger mortgage

3. **Equity building**: You're paying down a mortgage while tenants contribute — building wealth passively

4. **Tax deductions**: Expenses related to the rental portion (a proportional share of mortgage interest, property tax, insurance, utilities, maintenance) are tax-deductible

## The Numbers: Does It Work?

**Example — Ontario duplex:**
- Purchase price: $600,000
- Down payment: 10% = $60,000 (multi-unit primary)
- Mortgage: $540,000 at 5.5% over 25 years → ~$3,300/month
- Property tax + insurance: $500/month
- Total housing cost: $3,800/month

- Upstairs rent (your unit): you pay nothing in cash
- Basement rent: $1,800/month

**Your net housing cost: $3,800 − $1,800 = $2,000/month**

Compared to renting a similar unit for $2,500–$3,000/month, you're ahead — and building equity.

## Watch Out For

**Legal suites**: Ensure basement suites are legal and permitted in your municipality. Illegal suites create liability and insurance problems.

**Landlord responsibilities**: You're a landlord. Screen tenants carefully, know your province's Residential Tenancies Act, and budget for vacancies and repairs.

**Financing**: Some lenders apply rental income at 50–80% of actual rent for qualification purposes.

**Taxes**: Rental income is taxable. Keep excellent records of all rental-related expenses.

## Calculate Your House Hack

Use our free [House Hacking Calculator](/tools/finance/investing) to see your net monthly housing cost, equity buildup, and 30-year comparison against renting.
    `,
  },

  // ── Freelancing ────────────────────────────────────────────────
  {
    slug:     'freelance-hourly-rate-canada',
    title:    'How to Calculate Your Freelance Hourly Rate in Canada',
    excerpt:  'Setting your rate too low leaves money on the table. Too high and you lose clients. Here\'s the exact formula for calculating a sustainable freelance rate that covers taxes, expenses, and a livable income.',
    category: 'Freelancing',
    tag:      'freelance',
    icon:     '💼',
    readTime: '7 min',
    date:     '2026-04-05',
    content:  `
## Why Most Freelancers Underprice Themselves

The most common mistake: taking your desired salary and dividing by 2,080 (52 weeks × 40 hours). This ignores:

- **Non-billable time** (business development, admin, invoicing, professional development)
- **Self-employed taxes** (you pay both employer and employee CPP contributions)
- **No paid benefits** (health insurance, dental, vacation, sick days)
- **Business expenses** (software, equipment, liability insurance, professional fees)
- **Unpaid vacation** (every week off = lost income)

## The Formula

**Minimum viable hourly rate = (Annual income target + Annual expenses + Taxes) ÷ Billable hours**

### Step 1: Annual income target

What do you want to take home after taxes? Be specific. Include:
- Living expenses + lifestyle
- RRSP/TFSA contributions
- Emergency fund building
- Retirement savings target

Example: $80,000 desired net income

### Step 2: Annual business expenses

- Software and subscriptions: $2,400/year
- Equipment depreciation: $1,200/year
- Professional liability insurance: $1,500/year
- Professional development: $1,000/year
- Accounting fees: $1,500/year
- Marketing and website: $600/year
- Total: **$8,200/year**

### Step 3: Self-employment taxes (Canada)

As a self-employed Canadian, you pay:
- Both employee and employer CPP: ~11.9% on net business income up to ~$68,500
- Income tax on business income
- No EI unless you opt in voluntarily

Rough rule: budget **35–40% of gross income** for all taxes (income + CPP).

### Step 4: Calculate gross income needed

If you want $80,000 net and taxes are 37% of gross:

Gross needed = $80,000 ÷ (1 − 0.37) = **$126,984 gross**

Add expenses: $126,984 + $8,200 = **$135,184 gross revenue needed**

### Step 5: Billable hours per year

Full-time freelance = ~1,800 working hours per year.

Subtract:
- 3 weeks vacation: −120 hours
- Non-billable time (40%): −672 hours
- Remaining billable hours: **~1,000 hours/year**

Many freelancers aim for 1,000–1,200 billable hours. Below 1,000 is lean; above 1,400 is unsustainable long-term.

### Step 6: The rate

$135,184 ÷ 1,000 billable hours = **$135/hour**

## Sanity Check: Market Rates

Your calculated rate needs to be defensible in the market. Research:
- Job postings for your skills (contractor/consulting rates)
- Upwork market rates (filter by experience level)
- Industry surveys (Canadian IT: $75–$150+/hour; design: $50–$120/hour; writing: $50–$100/hour)

If your calculated rate is way above market, you need to either reduce your income target or increase your value proposition (niche expertise, faster delivery, specialised skills).

## Calculate Yours

Use our free [Freelance Rate Calculator](/tools/business/rate) to model your rate with your specific income target, expenses, tax rate, billable hours, and vacation weeks.
    `,
  },

  {
    slug:     'upwork-fee-guide',
    title:    'Upwork Fees Explained: How to Minimise What You Pay',
    excerpt:  'Upwork\'s fee structure can take 10–20% of your earnings. Here\'s exactly how the fees work, when they drop, and how to structure your work to pay as little as possible.',
    category: 'Freelancing',
    tag:      'freelance',
    icon:     '🏆',
    readTime: '5 min',
    date:     '2026-04-12',
    content:  `
## Upwork's Current Fee Structure (2026)

Upwork simplified their fee structure in recent years. The current model:

| Lifetime Billings with Client | Service Fee |
|------------------------------|-------------|
| First $500                   | 20%         |
| $500+                        | 10%         |

The fee is calculated per client relationship, not your total Upwork earnings. Every new client starts at 20% until you've billed them $500.

## What This Means in Practice

**New client, $100 project**: You earn $80 (Upwork keeps $20)

**Existing client, $500+ billed total**: You earn $90 per $100 (Upwork keeps $10)

**Strategy**: Build long-term relationships with fewer clients to maximize time at the 10% tier.

## How to Minimise Fees

### 1. Build long-term client relationships

The fastest path from 20% to 10% is $500 in billings with one client. On a $50/hour rate, that's 10 hours. After that, you're at 10% indefinitely with that client.

Repeat clients are more profitable than constantly finding new ones — better rates, faster onboarding, and lower fees.

### 2. Raise rates before starting new client relationships

Your first $500 with each client is at 20%. Price accordingly. A $75/hour rate nets $60/hour for new clients (20% fee). After $500 billed, it nets $67.50/hour.

### 3. Use the "Direct Contract" option for existing clients off-platform

Once you've established trust with a client on Upwork, they may be willing to work directly. Note: Upwork's terms require you to not bypass the platform during an active contract, but existing long-term clients can be moved off-platform after the original contract ends. Be aware of the 2-year rule.

### 4. Higher-value projects spend less time at 20%

A $5,000 project crosses the $500 threshold in the first 10% of work, so the 20% fee applies only to a small portion.

## Your True Effective Rate

At $75/hour:
- First 10 hours with new client (20% fee): **$60/hour net**
- All subsequent hours (10% fee): **$67.50/hour net**

Know your effective rate when quoting projects.

## Calculate Your Upwork Earnings

Use our free [Upwork Fee Calculator](/tools/business/saving) to see your exact take-home at different rates, hours, and lifetime billing levels. Also shows you exactly how much more you'd earn once you cross the $500 threshold.
    `,
  },

  // ── Developer Tools ────────────────────────────────────────────
  {
    slug:     'jwt-explained-developers',
    title:    'JWT Explained for Developers: What\'s Inside the Token?',
    excerpt:  'JSON Web Tokens are everywhere — authentication, APIs, microservices. This guide explains exactly what\'s inside a JWT, how to decode one, and when to use them vs alternatives.',
    category: 'Developer Tools',
    tag:      'devtools',
    icon:     '⌨️',
    readTime: '7 min',
    date:     '2026-04-20',
    content:  `
## What Is a JWT?

A JSON Web Token (JWT) is a compact, URL-safe way to represent claims between two parties. It's widely used for:

- **API authentication** — instead of sending credentials with every request
- **Single Sign-On (SSO)** — share authentication state across services
- **Information exchange** — pass verified data between microservices

## The Structure

A JWT consists of three Base64URL-encoded parts separated by dots:

\`\`\`
xxxxx.yyyyy.zzzzz
 ↑      ↑      ↑
Header Payload Signature
\`\`\`

### Header

\`\`\`json
{
  "alg": "HS256",
  "typ": "JWT"
}
\`\`\`

\`alg\` is the signing algorithm: HS256 (HMAC-SHA256), RS256 (RSA-SHA256), ES256 (ECDSA-SHA256).

### Payload (Claims)

\`\`\`json
{
  "sub": "1234567890",
  "email": "user@example.com",
  "name": "Rohit Jain",
  "role": "admin",
  "iat": 1716000000,
  "exp": 1716003600
}
\`\`\`

Standard claims:
- **sub**: Subject — typically user ID
- **iat**: Issued At — Unix timestamp
- **exp**: Expiration — Unix timestamp (token invalid after this)
- **iss**: Issuer — who created the token
- **aud**: Audience — who the token is intended for

### Signature

The signature is created by the server:

\`\`\`
HMACSHA256(
  base64UrlEncode(header) + "." + base64UrlEncode(payload),
  secretKey
)
\`\`\`

The signature verifies the token wasn't tampered with. Without the secret key, the signature can't be forged.

## Is a JWT Encrypted?

**No.** The header and payload are only Base64URL-encoded — anyone can decode them instantly. Don't put sensitive data (passwords, card numbers, SSNs) in a JWT payload.

The signature verifies integrity (wasn't tampered with) — not confidentiality.

To encrypt the payload, use JWE (JSON Web Encryption), a separate spec.

## JWT vs Sessions

| | JWT | Sessions |
|---|---|---|
| Storage | Client-side | Server-side |
| Stateless | Yes | No |
| Revocation | Hard (until expiry) | Easy |
| Scalable | Yes (no server state) | Requires shared session store |
| Token size | Larger (base64 payload) | Small (session ID only) |

**Use JWT when**: stateless authentication, microservices, mobile apps, public APIs.

**Use sessions when**: you need immediate revocation, simpler implementation, and you have a single server or shared session store.

## Common Security Mistakes

1. **Accepting "none" algorithm**: Always validate the algorithm — some libraries accept \`"alg": "none"\` which removes signature verification
2. **Long expiry times**: Short-lived access tokens (15–60 min) plus refresh tokens is best practice
3. **Storing in localStorage**: Vulnerable to XSS. Use httpOnly cookies for better security
4. **Not validating claims**: Always check \`exp\`, \`iss\`, and \`aud\` on the server

## Decode Any JWT

Use our free [JWT Decoder](/tools/dev/jwt) to decode any JWT token, see the full payload, and check if it's expired — all in your browser, nothing sent to any server.
    `,
  },

  {
    slug:     'regex-guide-developers',
    title:    'Regular Expressions: A Practical Guide for Developers',
    excerpt:  'Regex seems intimidating until it clicks. This guide teaches the most useful patterns with real-world examples for email validation, phone numbers, URLs, and more.',
    category: 'Developer Tools',
    tag:      'devtools',
    icon:     '⌨️',
    readTime: '8 min',
    date:     '2026-04-28',
    content:  `
## Why Regex Matters

Regular expressions let you match, extract, and transform text with a single pattern. Master the basics and you'll handle 90% of real-world text processing needs.

## The Building Blocks

### Character Classes

| Pattern | Matches |
|---------|---------|
| \`.\` | Any character except newline |
| \`\\d\` | Any digit (0-9) |
| \`\\w\` | Word character (a-z, A-Z, 0-9, _) |
| \`\\s\` | Whitespace (space, tab, newline) |
| \`[abc]\` | a, b, or c |
| \`[a-z]\` | Any lowercase letter |
| \`[^abc]\` | Not a, b, or c |

### Quantifiers

| Pattern | Meaning |
|---------|---------|
| \`*\` | 0 or more |
| \`+\` | 1 or more |
| \`?\` | 0 or 1 (optional) |
| \`{3}\` | Exactly 3 |
| \`{2,5}\` | Between 2 and 5 |
| \`{3,}\` | 3 or more |

### Anchors

| Pattern | Meaning |
|---------|---------|
| \`^\` | Start of string |
| \`$\` | End of string |
| \`\\b\` | Word boundary |

## Real-World Patterns

### Email (Simple)

\`\`\`
^[\\w.-]+@[\\w.-]+\\.[a-z]{2,}$
\`\`\`

Matches: user@example.com, name.surname@company.co.uk

### Canadian Phone Numbers

\`\`\`
^(\\+1)?[\\s.-]?\\(?[0-9]{3}\\)?[\\s.-]?[0-9]{3}[\\s.-]?[0-9]{4}$
\`\`\`

Matches: 416-555-0123, (416) 555-0123, +1 416 555 0123

### URLs

\`\`\`
https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\\.[a-z]{2,6}\\b[-a-zA-Z0-9@:%_+.~#?&/=]*
\`\`\`

### Postal Code (Canada)

\`\`\`
^[A-Za-z]\\d[A-Za-z][ -]?\\d[A-Za-z]\\d$
\`\`\`

Matches: M5V 3A8, K1A0A9

### Credit Card (basic format check)

\`\`\`
^\\d{4}[\\s-]?\\d{4}[\\s-]?\\d{4}[\\s-]?\\d{4}$
\`\`\`

## Capture Groups

Parentheses create capture groups — you can extract specific parts of a match.

\`\`\`javascript
const date = '2026-04-28';
const match = date.match(/^(\\d{4})-(\\d{2})-(\\d{2})$/);
// match[1] = '2026', match[2] = '04', match[3] = '28'
\`\`\`

Named groups (more readable):

\`\`\`javascript
const match = date.match(/^(?<year>\\d{4})-(?<month>\\d{2})-(?<day>\\d{2})$/);
// match.groups.year = '2026'
\`\`\`

## Flags

| Flag | Effect |
|------|--------|
| \`g\` | Global — find all matches |
| \`i\` | Case-insensitive |
| \`m\` | Multiline — ^ and $ match line start/end |
| \`s\` | Dot matches newlines |

## Common Mistakes

**Being too greedy**: \`.*\` matches as much as possible. Use \`.*?\` (lazy) to match as little as possible.

**Not escaping special characters**: \`.\`, \`*\`, \`+\`, \`?\`, \`(\`, \`)\`, \`[\`, \`]\`, \`{\`, \`}\`, \`^\`, \`$\`, \`|\`, \`\\\` all need escaping with \\\\ when used literally.

**Testing on too few examples**: A regex that works on your test cases may fail on edge cases. Always test thoroughly.

## Test Your Regex

Use our free [Regex Tester](/tools/dev/regex) to test patterns live with real input, see all matches highlighted, and test flags interactively — all in your browser.
    `,
  },

  // ── Network Tools ──────────────────────────────────────────────
  {
    slug:     'dns-explained',
    title:    'DNS Explained: How Domains Turn Into IP Addresses',
    excerpt:  'DNS is the phone book of the internet. Understanding it helps you debug website issues, set up custom domains, and understand how email delivery works.',
    category: 'Network Tools',
    tag:      'network',
    icon:     '🌐',
    readTime: '6 min',
    date:     '2026-05-05',
    content:  `
## What Is DNS?

The Domain Name System (DNS) translates human-readable domain names (like arhat.info) into IP addresses (like 104.21.18.74) that computers use to connect. Without DNS, you'd need to memorise IP addresses for every website.

## How a DNS Lookup Works

When you type "arhat.info" in your browser:

1. **Browser cache** — checked first; if the IP is cached, it's used immediately
2. **OS cache** (hosts file) — checked next
3. **Recursive resolver** (your ISP or 1.1.1.1) — asks for the answer on your behalf
4. **Root nameservers** — points to the .info TLD nameservers
5. **TLD nameservers** (.info) — points to arhat.info's authoritative nameservers
6. **Authoritative nameserver** — returns the actual IP address
7. **Result cached** for the TTL duration

This whole process typically takes 20–100ms on first lookup.

## DNS Record Types

| Type | Purpose | Example |
|------|---------|---------|
| **A** | Maps domain to IPv4 | arhat.info → 104.21.18.74 |
| **AAAA** | Maps domain to IPv6 | arhat.info → 2606:4700::... |
| **CNAME** | Alias to another domain | www → arhat.info |
| **MX** | Mail server for the domain | arhat.info → mail.arhat.info |
| **TXT** | Text data (SPF, DKIM, verification) | v=spf1 include:... |
| **NS** | Authoritative nameservers | arhat.info → ns1.cloudflare.com |
| **SOA** | Start of authority record | Administrative info |

## TTL: Why DNS Changes Take Time

Every DNS record has a TTL (Time To Live) in seconds. This tells resolvers how long to cache the record before checking again.

- TTL 300 (5 minutes): changes propagate in 5 minutes
- TTL 3600 (1 hour): changes take up to 1 hour
- TTL 86400 (24 hours): changes take up to 24 hours

**Before changing nameservers or DNS**: lower your TTL to 300 (5 minutes) a day in advance. After the change propagates, you can raise it back.

## Common DNS Issues and Fixes

**"DNS_PROBE_FINISHED_NXDOMAIN"**: Domain doesn't exist or DNS isn't configured. Check the domain exists and A/CNAME records are set.

**Email not delivering**: Check MX records exist and point to the right mail server. Verify SPF and DKIM TXT records.

**"Too many redirects"**: Often a CNAME pointing to itself or a redirect loop. Check your CNAME chain.

**Site not loading after migration**: DNS is still pointing to old server. Check TTL hasn't expired yet; wait for propagation.

## Check Any Domain's DNS

Use our free [DNS Lookup Tool](/tools/ip/dns) to check A, AAAA, MX, TXT, NS, CNAME, and SOA records for any domain instantly.
    `,
  },

  {
    slug:     'ssl-certificate-guide',
    title:    'SSL Certificates: What They Are and Why They Expire',
    excerpt:  'SSL certificates keep websites secure and trusted. Here\'s what they are, why they expire, what happens when they do, and how to monitor yours before it causes problems.',
    category: 'Network Tools',
    tag:      'network',
    icon:     '🌐',
    readTime: '5 min',
    date:     '2026-05-12',
    content:  `
## What Is an SSL Certificate?

An SSL/TLS certificate is a digital credential that:

1. **Encrypts traffic** between your browser and the server (the "S" in HTTPS)
2. **Verifies identity** — proves the website is who it claims to be
3. **Enables trust** — browsers show the padlock icon only with a valid certificate

Without a certificate, browsers show "Not Secure" warnings that drive users away.

## Certificate Types

| Type | Validation | Use Case |
|------|-----------|---------|
| **DV (Domain Validated)** | Domain ownership only | Most websites, blogs, apps |
| **OV (Organisation Validated)** | Company identity verified | Business websites |
| **EV (Extended Validated)** | Thorough company verification | Banks, e-commerce |
| **Wildcard** | Covers *.domain.com | All subdomains |
| **Multi-domain (SAN)** | Multiple domains | Multiple sites |

## Why Certificates Expire

Certificates expire to:
- Ensure cryptographic algorithms stay current
- Limit the damage if a certificate is compromised
- Force regular review of who has access

**Let's Encrypt** (free CA) issues 90-day certificates. Commercial CAs issue 1-year certificates (2-year max is no longer allowed by browsers since 2020).

## What Happens When a Certificate Expires

- Chrome, Firefox, Safari show a **full-page security warning**
- Users cannot easily bypass it (they have to click through several warnings)
- Google may demote your site in search rankings
- Email clients may reject email from your domain
- **Revenue loss**: most users abandon expired-cert sites immediately

A single forgotten renewal can take down revenue for hours or days.

## Certificate Monitoring

Set reminders at:
- **30 days before expiry**: Review and prepare renewal
- **14 days before expiry**: Renew immediately if not done
- **7 days before expiry**: Emergency if not renewed

Most hosting providers auto-renew Let's Encrypt certificates. Verify auto-renewal is working — it fails silently if there are configuration issues.

## Check Any Site's SSL Certificate

Use our free [SSL Certificate Checker](/tools/ip/ssl) to view any website's certificate expiry date, issuer, subject, and whether it's currently valid. Takes 2 seconds, no sign-up needed.
    `,
  },

  // ── Mortgage & Loans ───────────────────────────────────────────
  {
    slug:     '15-vs-30-year-mortgage',
    title:    '15-Year vs 30-Year Mortgage: The Math Behind the Decision',
    excerpt:  'The interest savings on a 15-year mortgage can be staggering. But the higher payment isn\'t right for everyone. Here\'s a data-driven framework for making this decision.',
    category: 'Mortgage & Loans',
    tag:      'mortgage',
    icon:     '🏡',
    readTime: '6 min',
    date:     '2026-05-20',
    content:  `
## The Core Trade-off

A 15-year mortgage charges lower interest and pays off faster. A 30-year mortgage has lower monthly payments and more cash flow flexibility.

On a $400,000 mortgage:

| | 15-Year (5.5%) | 30-Year (6.5%) |
|---|---|---|
| Monthly payment | $3,270 | $2,528 |
| Total interest | $188,600 | $510,400 |
| Interest saved | — | — |
| **Total paid** | **$588,600** | **$910,400** |

The 15-year saves **$321,800 in interest** — nearly the original loan amount.

But the 15-year costs $742 more per month. Over 15 years, that's $133,560 in extra payments. You "save" $321,800 but "spend" $133,560 more — net advantage of ~$188,000.

## The Opportunity Cost Argument

Some financial advisors argue: take the 30-year, invest the difference.

The math: invest $742/month for 15 years at 7% real return = **$232,000**. That partially offsets the interest savings from the 15-year.

But this requires discipline — actually investing the difference every month for 15 years. Most people don't. The 15-year forces the savings.

## When 15-Year Wins

- You have stable, high income
- You're committed to the higher payment
- You value guaranteed, risk-free return of avoiding interest
- You're within 15 years of retirement (no mortgage in retirement is valuable)
- Interest rates are high (the interest savings compound more)

## When 30-Year Wins

- Income is variable or uncertain
- You have high-interest debt that should be paid first
- You're maximising RRSP/TFSA contributions (registered accounts shelter growth)
- Investment returns are likely to exceed your mortgage rate (currently uncertain)
- You want flexibility in case of job loss

## The Canadian Context

Canadian mortgages work differently than US mortgages:
- Typical amortisation period: 25 years (30 with 20%+ down, or 35 for insured mortgages under new 2024 rules for some buyers)
- Rate terms are usually 5 years — you renegotiate every 5 years
- Mortgage interest is **not tax-deductible** in Canada (unlike the US)
- CMHC insurance required for down payments below 20%

The "15 vs 30" decision in Canada is more like "20 vs 25 years" but the same math applies.

## Run the Numbers

Use our free [Mortgage Calculator](/tools/finance/mortgage) to compare any two term lengths with your specific loan amount and interest rates. See the full amortization charts side by side.
    `,
  },

  // ── Personal Finance ───────────────────────────────────────────
  {
    slug:     'emergency-fund-guide',
    title:    'Emergency Fund: How Much, Where to Keep It, and Why It Matters',
    excerpt:  'An emergency fund is the foundation of every financial plan. Here\'s how to size yours correctly, where to keep it in Canada, and why this boring step is actually non-negotiable.',
    category: 'Personal Finance',
    tag:      'personal',
    icon:     '💸',
    readTime: '5 min',
    date:     '2026-06-01',
    content:  `
## What Is an Emergency Fund?

An emergency fund is liquid savings set aside specifically for unexpected expenses: job loss, medical bills, car repairs, home repairs, or any other unplanned financial shock.

It is not:
- An investment account
- A vacation fund
- A "general savings" fund
- Your RRSP or TFSA (though these could theoretically be used)

## How Much Do You Need?

The standard guidance: **3–6 months of essential expenses**.

| Situation | Target |
|-----------|--------|
| Stable dual-income household | 3 months |
| Single income or dependents | 6 months |
| Variable income or freelance | 6–9 months |
| Self-employed or commission-based | 9–12 months |

"Essential expenses" means the minimum to survive: housing, food, utilities, insurance, minimum debt payments, transportation to work.

**Example**: If your essential monthly expenses are $3,500, your 6-month target is $21,000.

## Where to Keep It in Canada

**Requirements**: liquid (accessible in 1–3 days), safe (not invested in stocks), earns some interest.

**Best options in Canada:**

| Institution | Account Type | Notes |
|------------|-------------|-------|
| EQ Bank | HISA | Consistently competitive rates |
| Oaken Financial | HISA | High rates, GIC options |
| Wealthsimple Cash | Cash account | Instant access |
| Simplii Financial | HISA | No monthly fees |
| Your bank's HISA | HISA | Convenient but lower rates |

**Do not keep your emergency fund** in: stocks, mutual funds, GICs (locked-in), or crypto. These can lose value or be inaccessible exactly when you need them most.

## The Order of Operations

Before building a 6-month fund, many experts recommend:

1. **$1,000–$2,000 mini emergency fund** first (handles most small emergencies)
2. **Pay off high-interest debt** (credit cards at 20%+ are guaranteed 20% returns)
3. **Build full 3–6 month emergency fund**
4. **Invest aggressively** once emergency fund is complete

## Common Mistakes

**Keeping too little**: One job loss or medical event wipes it out. Start small but grow it.

**Keeping too much**: More than 12 months sitting in cash is likely too conservative for most people — that money could be invested.

**Commingling with regular savings**: Keep the emergency fund in a separate account you don't touch. Out of sight, out of mind.

**Forgetting to replenish**: After using the emergency fund for an actual emergency, rebuilding it becomes the next financial priority.

## Calculate Your Target

Use our free [Emergency Fund Calculator](/tools/finance/emergency) to find your personalised target based on your expenses, job stability, and dependents — and see how long it takes to build at different monthly contribution rates.
    `,
  },

  {
    slug:     'inflation-salary-canada',
    title:    'How Inflation Is Silently Cutting Your Salary',
    excerpt:  'If you got a 3% raise but inflation was 4%, you took a pay cut. Here\'s how to calculate your real purchasing power and what to do about it in salary negotiations.',
    category: 'Personal Finance',
    tag:      'personal',
    icon:     '💸',
    readTime: '5 min',
    date:     '2026-06-10',
    content:  `
## The Silent Pay Cut

Inflation erodes purchasing power. If prices rise 4% this year and your salary doesn't, you can afford 4% less with the same paycheque.

A nominal raise that doesn't keep up with inflation is a real pay cut.

**Example**: $75,000 salary, 3.5% inflation for 3 years.

Real purchasing power after 3 years = $75,000 ÷ (1.035)^3 = **$67,000 in today's dollars**

You need **$83,100** in 3 years just to maintain the same lifestyle.

## Canada's Inflation History (Recent)

| Year | CPI Inflation |
|------|--------------|
| 2021 | 3.4% |
| 2022 | 6.8% (peak) |
| 2023 | 3.9% |
| 2024 | 2.4% |
| 2025 | ~2.1% (estimate) |

The Bank of Canada targets 2% inflation. The 2022 spike erased years of purchasing power gains for many Canadians.

## How to Use This in Salary Negotiations

### Calculate your real raise history

"I've received 2% raises for 3 years, but inflation averaged 4%. My real salary has declined by 6% in purchasing power."

This is a factual, non-confrontational way to frame an underpayment conversation.

### Research market rates

Glassdoor, LinkedIn Salary, Robert Half Salary Guide, and Indeed all publish Canadian salary data by role and location. Find your market rate and use it as a benchmark.

### Ask for more than inflation

Inflation-matching raises keep you standing still. A real raise is inflation + growth for performance or scope expansion.

### If a raise isn't possible, negotiate other compensation

- More vacation days
- Remote work flexibility
- Professional development budget
- RRSP matching
- Performance bonus tied to objectives

## The RRSP / TFSA Hedge

Money invested in an RRSP or TFSA earns returns that hopefully outpace inflation. Letting savings sit in a chequing account means inflation eats it.

The real return on investments = nominal return − inflation. At 7% nominal and 2.5% inflation, your real return is approximately 4.4%.

## Calculate the Impact

Use our free [Inflation & Salary Calculator](/tools/finance/inflation) to see exactly how much purchasing power your salary loses over any time period, and what salary you'd need to stay even.
    `,
  },
];

// ── Helper functions ─────────────────────────────────────────────
export const CATEGORIES = [
  { id:'fire',       label:'FIRE Planning',      color:'#e67e22', icon:'🔥' },
  { id:'investing',  label:'Investing',           color:'#2ecc71', icon:'📈' },
  { id:'canadian',   label:'Canadian Finance',    color:'#1abc9c', icon:'🍁' },
  { id:'realestate', label:'Real Estate',         color:'#4d8fff', icon:'🏠' },
  { id:'freelance',  label:'Freelancing',         color:'#b78cff', icon:'💼' },
  { id:'devtools',   label:'Developer Tools',     color:'#2ecc71', icon:'⌨️' },
  { id:'network',    label:'Network Tools',       color:'#00c8f0', icon:'🌐' },
  { id:'mortgage',   label:'Mortgage & Loans',    color:'#f1c40f', icon:'🏡' },
  { id:'personal',   label:'Personal Finance',    color:'#e74c3c', icon:'💸' },
];


export const getPostsByCategory = (tag) =>
  BLOG_POSTS.filter(p => p.tag === tag);

export const getRelatedPosts = (slug, tag, limit = 3) =>
  BLOG_POSTS.filter(p => p.slug !== slug && p.tag === tag).slice(0, limit);

export const getPostBySlug = (slug) =>
  BLOG_POSTS.find(p => p.slug === slug);
